import { DateTime } from '@ksyos/date-time';
import { Koi } from '@ksyos/koi';
import { Map } from 'immutable';
import type { ISchemaContainer } from './schemaContainer';
import { AvailabilityType } from '../enums/availabilityType';
import { DayOfWeek } from '../enums/dayOfWeek';
import { replaceKoiError } from './replaceKoiError';
import { validateDatetimeOverlap } from './utils/validateDatetimeOverlap';

export interface IAvailabilityAddEditValidated {
    availabilityType: AvailabilityType;
    dayOfWeek: DayOfWeek;
    employmentId?: string;
    enabled: boolean;
    endTime: string | null;
    id?: string;
    startTime: string | null;
}

interface IAvailability {
    enabled: boolean;
    endTime: string | null;
    startTime: string | null;
}

const availabilityAddEdit = Koi.object({
    availabilityType: Koi.koi().enum(AvailabilityType),
    dayOfWeek: Koi.koi().enum(DayOfWeek),
    enabled: Koi.boolean(),
});

export const availabilityAddEditSchema: ISchemaContainer<IAvailabilityAddEditValidated> = {
    schema: availabilityAddEdit
        .keys({
            employmentId: Koi.string().uuid().optional(),
            endTime: Koi.koi()
                .timeWithoutSeconds()
                .when('enabled', {
                    is: true,
                    otherwise: Koi.allow(null),
                    then: Koi.disallow('', null),
                })
                .error((errors) => replaceKoiError(errors, 'koi.timeWithoutSeconds', 'endTime.required')),
            startTime: Koi.koi()
                .timeWithoutSeconds()
                .when('enabled', {
                    is: true,
                    otherwise: Koi.allow(null),
                    then: Koi.disallow('', null),
                })
                .error((errors) => replaceKoiError(errors, 'koi.timeWithoutSeconds', 'startTime.required')),
        })
        .options({ convert: false, presence: 'required' }),
};

export function validateAvailabilityPeriod(
    availability: IAvailability,
    errors: Map<string, string | undefined>,
    errorKey: string
) {
    if (availability.startTime && availability.endTime) {
        const startTime = DateTime.parse(availability.startTime, 'HH:mm');
        const endTime = DateTime.parse(availability.endTime, 'HH:mm');

        if (!startTime.isBefore(endTime)) {
            return errors.set(errorKey, 'endTimeBeforeStartTime');
        }
    }

    return errors.remove(errorKey);
}

export function validateAvailabilityPeriodOverlap(
    day: { periodOne: IAvailability; periodTwo: IAvailability },
    errors: Map<string, string | undefined>,
    errorKey: string
) {
    if (
        day.periodOne.enabled &&
        day.periodOne.startTime &&
        day.periodOne.endTime &&
        day.periodTwo.enabled &&
        day.periodTwo.startTime &&
        day.periodTwo.endTime
    ) {
        const [resultOne, resultTwo] = validateDatetimeOverlap([
            transformToDateRange(day.periodOne),
            transformToDateRange(day.periodTwo),
        ]);

        if (resultOne.errors.size > 0 || resultTwo.errors.size > 0) {
            return errors.set(errorKey, 'timePeriodsOverlap');
        }
    }

    return errors.remove(errorKey);
}

function transformToDateRange(availability: IAvailability) {
    return {
        endDate: availability.endTime ? DateTime.parseExact(availability.endTime, 'HH:mm').toDate() : null,
        errors: Map<string, string | undefined>(),
        startDate: availability.startTime ? DateTime.parseExact(availability.startTime, 'HH:mm').toDate() : null,
    };
}
